import React, { useState, useRef, useEffect } from 'react';
import styles from './StrategyForm.module.css';

/**
 * Compact multi-select dropdown for choosing notification groups.
 * Shows a select-styled trigger ("N groups selected") that opens a
 * checkbox panel. Self-contained: manages its own open/close + click-outside.
 */
const NotificationGroupDropdown = ({ groups = [], selected = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = (id) => {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange([...set]);
  };

  const label =
    selected.length === 0
      ? 'Select groups'
      : `${selected.length} group${selected.length > 1 ? 's' : ''} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={styles.select}
        style={{
          textAlign: 'left',
          width: '100%',
          // Stable width: long labels truncate instead of widening the control.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            // Wider than the 180px trigger so long group names fit on one line.
            minWidth: 240,
            width: 'max-content',
            maxWidth: 320,
            background: 'var(--background-white)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            maxHeight: 220,
            overflowY: 'auto',
            padding: 'var(--spacing-sm)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          {groups.length === 0 ? (
            <div
              style={{
                padding: '6px 4px',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No groups available. Create one under Notification Query first.
            </div>
          ) : (
            groups.map((g) => (
              <label
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 4px',
                  cursor: 'pointer',
                  // Reset the global `label {}` uppercase + letter-spacing that
                  // leaks in from the non-module form CSS, and keep on one line.
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(g.id)}
                  onChange={() => toggle(g.id)}
                />
                <span>{g.query_title || `Group #${g.id}`}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationGroupDropdown;
